import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import EmailModal from './EmailModal';
import { useAuth } from './AuthContext';
import Pagination from './Pagination';
import DocumentKPIBar from './DocumentKPIBar';
import QuickFilters from './QuickFilters';
import GlobalSearch from './GlobalSearch';
import BulkActionsBar from './BulkActionsBar';
import ColumnManager from './ColumnManager';
import SavedViews from './SavedViews';
import AdvancedExport from './AdvancedExport';
import StatusBadge from './StatusBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Fonction pour calculer les informations de tarification d'un document
const calculateDocumentPricing = (document) => {
  const items = document.items || [];
  
  // Calculer les totaux
  const totalTarifTransporteur = items.reduce((sum, item) => sum + (item.tarif_transporteur_ht || 0) * (item.quantity || 1), 0);
  const totalMargeHt = items.reduce((sum, item) => sum + (item.marge_ht || 0) * (item.quantity || 1), 0);
  const totalMargeAvecTva = totalMargeHt * 1.18; // Marge TTC avec TVA 18%
  const totalTarifClient = items.reduce((sum, item) => sum + (item.unit_price || 0) * (item.quantity || 1), 0);
  
  // Calculer la marge moyenne pondérée
  const margeMovenPondere = totalTarifTransporteur > 0 ? 
    (totalMargeHt / totalTarifTransporteur) * 100 : 0;
  
  return {
    totalTarifTransporteur,
    totalMargeHt,
    totalMargeAvecTva,
    totalTarifClient,
    margeMovenPondere
  };
};

// Fonction pour obtenir le statut de paiement (seulement pour factures et bons de commande)
const getPaymentStatus = (document) => {
  // Pour les devis, il n'y a pas de notion de paiement
  if (document.type === 'devis') {
    return null; // Pas de statut de paiement pour les devis
  }
  
  const montantPaye = document.montant_paye || 0;
  const totalTtc = document.total_ttc || 0;
  
  if (montantPaye >= totalTtc) {
    return { status: 'paid', label: 'Payé', color: 'bg-green-100 text-green-800' };
  } else if (montantPaye > 0) {
    return { status: 'partial', label: 'Partiellement payé', color: 'bg-yellow-100 text-yellow-800' };
  } else {
    return { status: 'unpaid', label: 'Non payé', color: 'bg-red-100 text-red-800' };
  }
};

const DocumentList = ({ documents, onDocumentSelect, onOpenPaymentManager, onEditDocument, onStatusChange, onDeleteDocument, selectedDocuments = [], onSelectDocument, onSelectAllDocuments, onClearSelection, onRefresh }) => {
  const { user } = useAuth();
  
  // États pour la recherche avancée
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  
  // Filtres avancés
  const [filters, setFilters] = useState({
    type: 'all',
    statut: 'all',
    client: '',
    transporteur: '',
    opportunite: '',
    operation: '',
    dateStart: '',
    dateEnd: '',
    montantMin: '',
    montantMax: '',
    paymentStatus: 'all'
  });
  
  // États existants
  const [filterType, setFilterType] = useState('all');
  const [filterOpportunity, setFilterOpportunity] = useState('all');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDocumentForEmail, setSelectedDocumentForEmail] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedDocumentForStatus, setSelectedDocumentForStatus] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDocumentForDelete, setSelectedDocumentForDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // États pour le tri
  const [sortField, setSortField] = useState('date_creation');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' ou 'desc'
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // États pour la recherche globale
  const [globalSearchTerm, setGlobalSearchTerm] = useState('');
  
  // États pour les filtres rapides
  const [quickFilters, setQuickFilters] = useState([]);
  
  // États pour les listes de données pour les filtres
  const [clients, setClients] = useState([]);
  const [transporteurs, setTransporteurs] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [operations, setOperations] = useState([]);

  // États Phase 2 - Gestionnaire de colonnes
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [showSavedViews, setShowSavedViews] = useState(false);
  const [showAdvancedExport, setShowAdvancedExport] = useState(false);
  const [showQuickFilters, setShowQuickFilters] = useState(false); // Masqué par défaut
  const [showKPIBar, setShowKPIBar] = useState(false); // Masqué par défaut
  
  // Définition de toutes les colonnes disponibles
  const allColumns = [
    { id: 'checkbox', label: '☑️', description: 'Sélection multiple', essential: true },
    { id: 'reference', label: 'Référence', description: 'Numéro de référence du document' },
    { id: 'type', label: 'Type', description: 'Type de document (Devis, Facture, etc.)' },
    { id: 'client', label: 'Client', description: 'Nom du client' },
    { id: 'type_contact', label: 'Type Contact', description: 'Type de contact (Prospect/Client)' },
    { id: 'opportunite', label: 'Opportunité', description: 'Opportunité liée' },
    { id: 'operation', label: 'Opération', description: 'Numéro d\'opération' },
    { id: 'date_creation', label: 'Créé le', description: 'Date de création' },
    { id: 'date_maj', label: 'MAJ', description: 'Dernière mise à jour' },
    { id: 'tarif_transporteur', label: 'Tarif Transp.', description: 'Tarif transporteur HT' },
    { id: 'marge_pourcent', label: 'Marge %', description: 'Marge en pourcentage' },
    { id: 'marge_ht', label: 'Marge HT', description: 'Marge hors taxe' },
    { id: 'marge_ttc', label: 'Marge TTC', description: 'Marge TTC' },
    { id: 'tarif_client', label: 'Tarif Client', description: 'Tarif client HT' },
    { id: 'total', label: 'Total TTC', description: 'Montant total TTC' },
    { id: 'statut_doc', label: 'Statut Doc', description: 'Statut du document' },
    { id: 'statut_paiement', label: 'Statut Paiement', description: 'Statut de paiement' },
    { id: 'reste', label: 'Reste à payer', description: 'Montant restant à payer' },
    { id: 'actions', label: 'Actions', description: 'Actions disponibles', essential: true }
  ];
  
  // Colonnes visibles par défaut
  const defaultVisibleColumns = ['checkbox', 'reference', 'type', 'client', 'type_contact', 'opportunite', 
                                  'operation', 'date_creation', 'total', 'statut_doc', 
                                  'statut_paiement', 'reste', 'actions'];
  
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Charger depuis localStorage ou utiliser les colonnes par défaut
    const saved = localStorage.getItem('documentListVisibleColumns');
    return saved ? JSON.parse(saved) : defaultVisibleColumns;
  });

  // Charger les données pour les filtres
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };
        
        // Charger les clients
        const clientsResponse = await axios.get(`${API}/crm/clients`, { headers });
        setClients(clientsResponse.data || []);
        
        // Charger les transporteurs
        const transporteursResponse = await axios.get(`${API}/transporteurs`, { headers });
        setTransporteurs(transporteursResponse.data || []);
        
        // Charger les opportunités
        const opportunitiesResponse = await axios.get(`${API}/opportunities`, { headers });
        setOpportunities(opportunitiesResponse.data || []);
        
        // Charger les opérations
        const operationsResponse = await axios.get(`${API}/operations`, { headers });
        setOperations(operationsResponse.data || []);
        
      } catch (error) {
        console.error('Erreur lors du chargement des données de filtres:', error);
      }
    };
    
    loadFilterData();
  }, []);

  // Fonction pour mettre à jour les filtres
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Fonction pour réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      type: 'all',
      statut: 'all',
      client: '',
      transporteur: '',
      opportunite: '',
      operation: '',
      dateStart: '',
      dateEnd: '',
      montantMin: '',
      montantMax: '',
      paymentStatus: 'all'
    });
    setSearchTerm('');
  };

  // Fonction pour marquer un document comme vu
  const markDocumentAsViewed = async (documentId) => {
    try {
      await axios.put(`${API}/documents/${documentId}/mark-viewed`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
    } catch (error) {
      console.error('Erreur lors du marquage comme vu:', error);
    }
  };

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };
  
  // Handler pour la recherche globale
  const handleGlobalSearch = (searchTerm) => {
    setGlobalSearchTerm(searchTerm);
    setCurrentPage(1); // Reset à la première page
  };
  
  // Handler pour les filtres rapides
  const handleQuickFilter = (filter) => {
    if (filter === 'clear') {
      setQuickFilters([]);
    } else {
      setQuickFilters(prev => {
        if (prev.includes(filter)) {
          return prev.filter(f => f !== filter);
        } else {
          return [...prev, filter];
        }
      });
    }
    setCurrentPage(1); // Reset à la première page
  };
  
  // Handler pour les actions en masse
  const handleBulkAction = async (action, value) => {
    if (action === 'deselect_all') {
      if (onClearSelection) onClearSelection();
      return;
    }
    
    if (action === 'status') {
      // Changer le statut de tous les documents sélectionnés
      try {
        const token = localStorage.getItem('token');
        
        // Utiliser l'appel API direct pour garantir le fonctionnement
        for (const doc of selectedDocuments) {
          await axios.put(
            `${API}/documents/${doc.id}`,
            { statut: value },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
        }
        
        // Rafraîchir la liste après changement de statut
        if (onRefresh) {
          await onRefresh();
        }
        
        alert(`Statut changé pour ${selectedDocuments.length} document(s)`);
        if (onClearSelection) onClearSelection();
      } catch (error) {
        console.error('Erreur lors du changement de statut:', error);
        alert(`Erreur lors du changement de statut: ${error.message || 'Erreur inconnue'}`);
      }
    } else if (action === 'export') {
      // Ouvrir le modal d'export avancé
      setShowAdvancedExport(true);
    } else if (action === 'delete') {
      if (window.confirm(`Supprimer ${selectedDocuments.length} document(s) ?`)) {
        try {
          // Utiliser le callback du parent pour gérer la suppression
          if (onDeleteDocument) {
            for (const doc of selectedDocuments) {
              await onDeleteDocument(doc.id);
            }
          } else {
            // Fallback : appel API direct si le callback n'existe pas
            for (const doc of selectedDocuments) {
              await axios.delete(`${API}/documents/${doc.id}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
              });
            }
          }
          
          // Rafraîchir la liste après suppression
          if (onRefresh) {
            await onRefresh();
          }
          
          alert(`${selectedDocuments.length} document(s) supprimé(s)`);
          if (onClearSelection) onClearSelection();
        } catch (error) {
          console.error('Erreur lors de la suppression:', error);
          alert(`Erreur lors de la suppression: ${error.message || 'Erreur inconnue'}`);
        }
      }
    } else if (action === 'download') {
      // Télécharger les PDFs des documents sélectionnés
      try {
        const token = localStorage.getItem('token');
        
        alert(`Téléchargement de ${selectedDocuments.length} document(s) en cours...`);
        
        // Télécharger chaque document en PDF
        for (const doc of selectedDocuments) {
          try {
            const response = await axios.get(
              `${API}/documents/${doc.id}/pdf`,
              { 
                headers: { 'Authorization': `Bearer ${token}` },
                responseType: 'blob'
              }
            );
            
            // Créer un blob et télécharger
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${doc.reference || `document-${doc.id}`}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            // Délai pour éviter de surcharger le navigateur
            if (selectedDocuments.length > 1) {
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          } catch (docError) {
            console.error(`Erreur pour le document ${doc.reference}:`, docError);
            alert(`Erreur lors du téléchargement du document ${doc.reference}`);
          }
        }
        
        alert(`${selectedDocuments.length} document(s) téléchargé(s) !`);
        if (onClearSelection) onClearSelection();
      } catch (error) {
        console.error('Erreur lors du téléchargement:', error);
        alert(`Erreur lors du téléchargement: ${error.message || 'Erreur inconnue'}`);
      }
    } else if (action === 'email') {
      // Ouvrir le modal d'envoi d'email avec les documents sélectionnés
      alert(`Envoi de ${selectedDocuments.length} document(s) par email - Fonctionnalité à venir`);
      // TODO: Implémenter le modal d'envoi d'emails groupés
    }
  };

  // Handler Phase 2 - Gestion des colonnes
  const handleColumnsChange = (newVisibleColumns) => {
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('documentListVisibleColumns', JSON.stringify(newVisibleColumns));
  };

  // Handler Phase 2 - Charger une vue sauvegardée
  const handleLoadView = (view) => {
    // Charger les filtres
    if (view.filters) {
      if (view.filters.quickFilters) {
        setQuickFilters(view.filters.quickFilters);
      }
      if (view.filters.searchTerm) {
        setGlobalSearchTerm(view.filters.searchTerm);
      }
    }
    
    // Charger le tri
    if (view.sort) {
      setSortField(view.sort.field);
      setSortOrder(view.sort.order);
    }
    
    // Charger les colonnes
    if (view.columns) {
      setVisibleColumns(view.columns);
      localStorage.setItem('documentListVisibleColumns', JSON.stringify(view.columns));
    }
    
    alert(`Vue "${view.name}" chargée avec succès!`);
  };

  // Handler Phase 2 - Dupliquer un document
  const handleDuplicateDocument = async (document) => {
    if (!window.confirm(`Dupliquer le document ${document.reference} ?`)) {
      return;
    }
    
    try {
      // Créer une copie du document sans l'ID
      const duplicatedDoc = {
        ...document,
        id: undefined,
        reference: undefined, // Le backend génèrera une nouvelle référence
        date_creation: undefined,
        date_derniere_mise_a_jour: undefined,
        statut: 'brouillon',
        montant_paye: 0,
        reste_a_payer: document.total_ttc || 0
      };
      
      const token = localStorage.getItem('token');
      await axios.post(`${API}/documents`, duplicatedDoc, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      alert('Document dupliqué avec succès!');
      // Recharger la liste si la fonction existe
      if (typeof window.refreshDocuments === 'function') {
        window.refreshDocuments();
      }
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      alert('Erreur lors de la duplication du document');
    }
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return '↕️'; // Icône neutre
    }
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Fonction helper pour vérifier si une colonne est visible
  const isColumnVisible = (columnId) => {
    return visibleColumns.includes(columnId);
  };

  const handleStatusChange = async (document, newStatus) => {
    try {
      await axios.post(`${API}/documents/${document.id}/status`, { status: newStatus });
      if (onStatusChange) {
        onStatusChange(document.id, newStatus);
      }
      alert('Statut mis à jour avec succès');
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      alert('Erreur lors du changement de statut');
    }
  };

  const openStatusModal = (document) => {
    setSelectedDocumentForStatus(document);
    setShowStatusModal(true);
  };

  const openDeleteModal = (document) => {
    setSelectedDocumentForDelete(document);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedDocumentForDelete(null);
  };

  const handleDeleteDocument = async () => {
    if (!selectedDocumentForDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`${API}/documents/${selectedDocumentForDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Document supprimé avec succès');
        closeDeleteModal();
        if (onDeleteDocument) {
          onDeleteDocument(selectedDocumentForDelete.id);
        }
      } else {
        const error = await response.json();
        alert(`Erreur lors de la suppression: ${error.detail || 'Erreur inconnue'}`);
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression du document');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredDocuments = useMemo(() => {
    return documents
    .filter(doc => {
      // Recherche globale (cherche partout)
      const matchesGlobalSearch = !globalSearchTerm ||
        doc.client?.name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        doc.reference?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        doc.objet?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        doc.emetteur?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        doc.statut?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        doc.type?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        doc.transporteur_name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        String(doc.total_ttc || '').includes(globalSearchTerm) ||
        String(doc.reste_a_payer || '').includes(globalSearchTerm);
      
      // Recherche textuelle de base (compatibilité)
      const matchesSearch = !searchTerm || 
        doc.client?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.objet?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.emetteur?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtres rapides
      let matchesQuickFilters = true;
      if (quickFilters.length > 0) {
        matchesQuickFilters = quickFilters.some(filter => {
          // Filtres de type de document
          if (filter === 'type_devis') {
            return doc.type === 'devis';
          }
          if (filter === 'type_bon_commande') {
            return doc.type === 'bon_commande';
          }
          if (filter === 'type_facture') {
            return doc.type === 'facture';
          }
          
          // Filtres de statut document
          if (['brouillon', 'envoyé', 'acceptée', 'refusée', 'expiré', 'annulé'].includes(filter)) {
            return doc.statut === filter;
          }
          
          // Filtres de statut paiement
          if (filter === 'non_paye') {
            return doc.type !== 'devis' && (doc.montant_paye || 0) === 0 && (doc.total_ttc || 0) > 0;
          }
          if (filter === 'partiel') {
            return doc.type !== 'devis' && (doc.montant_paye || 0) > 0 && (doc.reste_a_payer || 0) > 0;
          }
          if (filter === 'paye') {
            return doc.type !== 'devis' && (doc.reste_a_payer || 0) === 0 && (doc.montant_paye || 0) > 0;
          }
          
          // Filtres de période
          const docDate = new Date(doc.date_creation || doc.date);
          const today = new Date();
          
          if (filter === 'today') {
            return docDate.toDateString() === today.toDateString();
          }
          if (filter === 'this_week') {
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return docDate >= weekAgo;
          }
          if (filter === 'this_month') {
            return docDate.getMonth() === today.getMonth() && docDate.getFullYear() === today.getFullYear();
          }
          
          // Filtres de workflow (Prospects vs Clients basés sur type_contact)
          if (filter === 'offres_prospects') {
            // Documents pour prospects (type_contact = "Client potentiel")
            return doc.client?.type_contact === 'Client potentiel';
          }
          if (filter === 'documents_clients') {
            // Documents pour clients actuels (type_contact = "Client actuel")
            return doc.client?.type_contact === 'Client actuel';
          }
          if (filter === 'autres') {
            // Autres types de contacts ou sans type_contact
            return doc.client?.type_contact === 'Autre' || !doc.client?.type_contact;
          }
          
          return true;
        });
      }
      
      // Filtres avancés
      const matchesType = filters.type === 'all' || doc.type === filters.type;
      
      const matchesStatut = filters.statut === 'all' || doc.statut === filters.statut;
      
      const matchesClient = !filters.client || 
        doc.client?.name?.toLowerCase().includes(filters.client.toLowerCase());
      
      const matchesTransporteur = !filters.transporteur || 
        (doc.transporteur_name && doc.transporteur_name.toLowerCase().includes(filters.transporteur.toLowerCase()));
      
      const matchesOpportunite = !filters.opportunite || 
        (doc.opportunite_id && opportunities.find(opp => 
          opp.id === doc.opportunite_id && 
          opp.nom_opportunite?.toLowerCase().includes(filters.opportunite.toLowerCase())
        ));
      
      const matchesOperation = !filters.operation || 
        (doc.operation_id && operations.find(op => 
          op.id === doc.operation_id && 
          (op.numero_operation?.toLowerCase().includes(filters.operation.toLowerCase()) ||
           op.description?.toLowerCase().includes(filters.operation.toLowerCase()))
        ));
      
      // Filtres de date
      const docDate = new Date(doc.date_creation || doc.date || 0);
      const matchesDateStart = !filters.dateStart || docDate >= new Date(filters.dateStart);
      const matchesDateEnd = !filters.dateEnd || docDate <= new Date(filters.dateEnd + 'T23:59:59');
      
      // Filtres de montant
      const docAmount = doc.total_ttc || 0;
      const matchesMontantMin = !filters.montantMin || docAmount >= parseFloat(filters.montantMin);
      const matchesMontantMax = !filters.montantMax || docAmount <= parseFloat(filters.montantMax);
      
      // Filtre de statut de paiement (seulement pour factures et bons de commande)
      let matchesPaymentStatus = true;
      if (filters.paymentStatus !== 'all') {
        if (doc.type === 'devis') {
          // Pour les devis, ignorer le filtre de paiement
          matchesPaymentStatus = true;
        } else {
          const paymentInfo = getPaymentStatus(doc);
          matchesPaymentStatus = paymentInfo && paymentInfo.status === filters.paymentStatus;
        }
      }
      
      // Filtres existants pour compatibilité
      const matchesTypeOld = filterType === 'all' || doc.type === filterType;
      const matchesOpportunityOld = filterOpportunity === 'all' || 
                                (filterOpportunity === 'linked' && doc.opportunite_id) ||
                                (filterOpportunity === 'unlinked' && !doc.opportunite_id);
      
      return matchesGlobalSearch && matchesQuickFilters && matchesSearch && matchesType && matchesStatut && matchesClient && 
             matchesTransporteur && matchesOpportunite && matchesOperation &&
             matchesDateStart && matchesDateEnd && matchesMontantMin && 
             matchesMontantMax && matchesPaymentStatus && matchesTypeOld && matchesOpportunityOld;
    })
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'reference':
          aValue = a.reference || '';
          bValue = b.reference || '';
          break;
        case 'client':
          aValue = a.client?.name || '';
          bValue = b.client?.name || '';
          break;
        case 'type_contact':
          // Tri par type de contact avec ordre: Client actuel > Client potentiel > Autre
          const typeOrder = { 'Client actuel': 3, 'Client potentiel': 2, 'Autre': 1 };
          aValue = typeOrder[a.client?.type_contact] || 0;
          bValue = typeOrder[b.client?.type_contact] || 0;
          break;
        case 'date_creation':
          aValue = new Date(a.date_creation || a.date || 0);
          bValue = new Date(b.date_creation || b.date || 0);
          break;
        case 'date_derniere_mise_a_jour':
          aValue = new Date(a.date_derniere_mise_a_jour || a.date || 0);
          bValue = new Date(b.date_derniere_mise_a_jour || b.date || 0);
          break;
        case 'total_ttc':
          aValue = a.total_ttc || 0;
          bValue = b.total_ttc || 0;
          break;
        case 'statut':
          aValue = a.statut || '';
          bValue = b.statut || '';
          break;
        case 'type':
          aValue = a.type || '';
          bValue = b.type || '';
          break;
        case 'opportunite_id':
          // Tri par nom d'opportunité
          const aOpp = opportunities.find(opp => opp.id === a.opportunite_id);
          const bOpp = opportunities.find(opp => opp.id === b.opportunite_id);
          aValue = aOpp?.nom_opportunite || '';
          bValue = bOpp?.nom_opportunite || '';
          break;
        case 'operation_id':
          // Tri par numéro d'opération
          const aOp = operations.find(op => op.id === a.operation_id);
          const bOp = operations.find(op => op.id === b.operation_id);
          aValue = aOp?.numero_operation || '';
          bValue = bOp?.numero_operation || '';
          break;
        case 'tarif_transporteur':
          // Calculer le tarif transporteur
          const aPricing = calculateDocumentPricing(a);
          const bPricing = calculateDocumentPricing(b);
          aValue = aPricing.totalTarifTransporteur || 0;
          bValue = bPricing.totalTarifTransporteur || 0;
          break;
        case 'marge_pourcent':
          // Calculer la marge en pourcentage
          const aPricingMarge = calculateDocumentPricing(a);
          const bPricingMarge = calculateDocumentPricing(b);
          aValue = aPricingMarge.margeMovenPondere || 0;
          bValue = bPricingMarge.margeMovenPondere || 0;
          break;
        case 'marge_ht':
          // Calculer la marge HT
          const aPricingHt = calculateDocumentPricing(a);
          const bPricingHt = calculateDocumentPricing(b);
          aValue = aPricingHt.totalMargeHt || 0;
          bValue = bPricingHt.totalMargeHt || 0;
          break;
        case 'marge_ttc':
          // Calculer la marge TTC
          const aPricingTtc = calculateDocumentPricing(a);
          const bPricingTtc = calculateDocumentPricing(b);
          aValue = aPricingTtc.totalMargeAvecTva || 0;
          bValue = bPricingTtc.totalMargeAvecTva || 0;
          break;
        case 'tarif_client':
          // Calculer le tarif client
          const aPricingClient = calculateDocumentPricing(a);
          const bPricingClient = calculateDocumentPricing(b);
          aValue = aPricingClient.totalTarifClient || 0;
          bValue = bPricingClient.totalTarifClient || 0;
          break;
        case 'statut_paiement':
          // Tri par statut de paiement
          const aPayment = getPaymentStatus(a);
          const bPayment = getPaymentStatus(b);
          // Ordre: paid > partial > unpaid > null
          const paymentOrder = { 'paid': 3, 'partial': 2, 'unpaid': 1 };
          aValue = aPayment ? (paymentOrder[aPayment.status] || 0) : 0;
          bValue = bPayment ? (paymentOrder[bPayment.status] || 0) : 0;
          break;
        case 'reste_a_payer':
          // Tri par reste à payer
          aValue = a.reste_a_payer || 0;
          bValue = b.reste_a_payer || 0;
          break;
        default:
          aValue = a[sortField] || '';
          bValue = b[sortField] || '';
      }
      
      // Gestion du tri pour différents types de données
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        // Tri alphabétique
        const comparison = String(aValue).localeCompare(String(bValue));
        return sortOrder === 'asc' ? comparison : -comparison;
      }
    });
  }, [documents, globalSearchTerm, quickFilters, searchTerm, filters, filterType, filterOpportunity, 
      sortField, sortOrder, opportunities, operations]);

  // Appliquer la pagination sur les documents filtrés
  const paginatedDocuments = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = currentPage * itemsPerPage;
    return filteredDocuments.slice(startIndex, endIndex);
  }, [filteredDocuments, currentPage, itemsPerPage]);

  const downloadPDF = async (documentId, reference) => {
    try {
      const response = await axios.get(`${API}/documents/${documentId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      // Create a temporary link element
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `${reference}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement du PDF');
    }
  };

  const handleSendEmail = (document) => {
    setSelectedDocumentForEmail(document);
    setShowEmailModal(true);
  };

  const handleEmailSent = () => {
    setShowEmailModal(false);
    setSelectedDocumentForEmail(null);
    // Optionally refresh the documents list or show a success message
  };

  // Fonction pour gérer la consultation d'un document
  const handleDocumentView = async (document) => {
    // Marquer le document comme vu s'il est nouveau
    if (document.est_nouveau) {
      await markDocumentAsViewed(document.id);
    }
    
    // Appeler la fonction de sélection du parent si elle existe
    if (onDocumentSelect) {
      onDocumentSelect(document);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-green-600 mb-4">Liste des documents</h3>
        
        {/* Toggle Résumé KPIs */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowKPIBar(!showKPIBar)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg 
              className={`w-4 h-4 transform transition-transform ${showKPIBar ? 'rotate-90' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-medium">
              {showKPIBar ? 'Masquer' : 'Afficher'} le résumé 📊
            </span>
          </button>
        </div>
        
        {/* KPI Bar */}
        {showKPIBar && (
          <DocumentKPIBar documents={documents} />
        )}
        
        {/* Recherche Globale */}
        <GlobalSearch onSearch={handleGlobalSearch} />
        
        {/* Toggle Filtres Rapides */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowQuickFilters(!showQuickFilters)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <svg 
              className={`w-4 h-4 transform transition-transform ${showQuickFilters ? 'rotate-90' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="font-medium">
              {showQuickFilters ? 'Masquer' : 'Afficher'} les filtres rapides
            </span>
            {quickFilters.length > 0 && (
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium">
                {quickFilters.length} actif{quickFilters.length > 1 ? 's' : ''}
              </span>
            )}
          </button>
        </div>
        
        {/* Filtres Rapides */}
        {showQuickFilters && (
          <QuickFilters 
            documents={documents} 
            activeFilters={quickFilters} 
            onFilterChange={handleQuickFilter} 
          />
        )}
        
        {/* Barre d'outils Phase 2 */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <button
            onClick={() => setShowColumnManager(true)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm font-medium"
            title="Gérer les colonnes"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
            Colonnes
          </button>
          
          <button
            onClick={() => setShowSavedViews(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
            title="Vues sauvegardées"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            Vues sauvegardées
          </button>
          
          <button
            onClick={() => setShowAdvancedExport(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
            title="Export avancé"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export avancé
          </button>
          
          <div className="ml-auto text-sm text-gray-500 flex items-center gap-2">
            <span className="bg-gray-100 px-3 py-2 rounded-lg">
              {visibleColumns.length} colonnes visibles
            </span>
          </div>
        </div>
        
        {/* Barre de recherche et bouton recherche avancée */}
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            placeholder="Recherche rapide par client, référence, objet ou émetteur..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
          />
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className={`px-4 py-3 rounded-lg font-medium transition-colors ${
              showAdvancedSearch 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            🔍 Recherche avancée
          </button>
          {(Object.values(filters).some(f => f && f !== 'all') || searchTerm) && (
            <button
              onClick={resetFilters}
              className="px-4 py-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              ✕ Effacer
            </button>
          )}
        </div>

        {/* Filtres avancés */}
        {showAdvancedSearch && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
            <h4 className="text-md font-semibold text-gray-700 mb-3">🔍 Recherche avancée</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Type de document */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de document</label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Tous les types</option>
                  <option value="devis">📄 Devis</option>
                  <option value="bon_commande">📋 Bon de commande</option>
                  <option value="facture">💰 Facture</option>
                </select>
              </div>

              {/* Statut du document */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={filters.statut}
                  onChange={(e) => handleFilterChange('statut', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="brouillon">📝 Brouillon</option>
                  <option value="envoyé">📤 Envoyé</option>
                  <option value="acceptée">✅ Accepté</option>
                  <option value="refusée">❌ Refusé</option>
                  <option value="en_cours">⏳ En cours</option>
                  <option value="terminée">✅ Terminé</option>
                  <option value="payé">💰 Payé</option>
                  <option value="encaissé">💰 Encaissé</option>
                </select>
              </div>

              {/* Client/Société */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client/Société</label>
                <input
                  type="text"
                  placeholder="Nom du client..."
                  value={filters.client}
                  onChange={(e) => handleFilterChange('client', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Transporteur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transporteur</label>
                <input
                  type="text"
                  placeholder="Nom du transporteur..."
                  value={filters.transporteur}
                  onChange={(e) => handleFilterChange('transporteur', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Opportunité */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opportunité</label>
                <input
                  type="text"
                  placeholder="Nom de l'opportunité..."
                  value={filters.opportunite}
                  onChange={(e) => handleFilterChange('opportunite', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Opération */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opération</label>
                <input
                  type="text"
                  placeholder="N° opération ou description..."
                  value={filters.operation}
                  onChange={(e) => handleFilterChange('operation', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Statut de paiement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut de paiement (factures/bons de commande)</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Tous les paiements</option>
                  <option value="paid">💰 Payé</option>
                  <option value="partial">Partiellement payé</option>
                  <option value="unpaid">❌ Non payé</option>
                </select>
              </div>

              {/* Date de début */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                <input
                  type="date"
                  value={filters.dateStart}
                  onChange={(e) => handleFilterChange('dateStart', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Date de fin */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                <input
                  type="date"
                  value={filters.dateEnd}
                  onChange={(e) => handleFilterChange('dateEnd', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Montant minimum */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant minimum (FCFA)</label>
                <input
                  type="number"
                  placeholder="0"
                  value={filters.montantMin}
                  onChange={(e) => handleFilterChange('montantMin', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Montant maximum */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Montant maximum (FCFA)</label>
                <input
                  type="number"
                  placeholder="999999999"
                  value={filters.montantMax}
                  onChange={(e) => handleFilterChange('montantMax', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Filtres rapides (compatibilité) */}
        <div className="flex gap-4 mb-4">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
          >
            <option value="all">Tous les types</option>
            <option value="devis">Devis</option>
            <option value="bon_commande">Bon de commande</option>
            <option value="facture">Facture</option>
          </select>
          <select
            value={filterOpportunity}
            onChange={(e) => setFilterOpportunity(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary focus:border-baobab-primary"
          >
            <option value="all">Toutes les liaisons</option>
            <option value="linked">🎯 Avec opportunité</option>
            <option value="unlinked">❌ Sans opportunité</option>
          </select>
        </div>

        {/* Statistiques dynamiques basées sur les filtres */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{filteredDocuments.length}</div>
            <div className="text-sm text-blue-800">Documents trouvés</div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {filteredDocuments.filter(d => d.opportunite_id).length}
            </div>
            <div className="text-sm text-green-800">Avec opportunité</div>
          </div>
          <div className="bg-orange-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">
              {filteredDocuments.filter(d => d.type !== 'devis' && getPaymentStatus(d)?.status === 'unpaid').length}
            </div>
            <div className="text-sm text-orange-800">Non payés (hors devis)</div>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">
              {filteredDocuments.filter(d => d.opportunite_id && d.operation_id).length}
            </div>
            <div className="text-sm text-purple-800">Avec opération</div>
          </div>
          <div className="bg-yellow-50 p-3 rounded-lg">
            <div className="text-lg font-bold text-yellow-600">
              {filteredDocuments.reduce((sum, d) => sum + (d.total_ttc || 0), 0).toLocaleString('fr-FR')} FCFA
            </div>
            <div className="text-sm text-yellow-800">Montant total</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Liste des documents */}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-gray-200">
              <tr>
                <th className="text-left py-2 px-2 font-semibold text-gray-700 w-10">
                  <input
                    type="checkbox"
                    checked={selectedDocuments.length === documents.length && documents.length > 0}
                    onChange={(e) => onSelectAllDocuments && onSelectAllDocuments(e.target.checked)}
                    className="w-3 h-3 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                  />
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('reference')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>Ref</span>
                    <span className="text-xs">{getSortIcon('reference')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('type')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>Type</span>
                    <span className="text-xs">{getSortIcon('type')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('client')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>Client</span>
                    <span className="text-xs">{getSortIcon('client')}</span>
                  </button>
                </th>
                {isColumnVisible('type_contact') && (
                  <th className="text-left py-2 px-2 font-semibold text-gray-700">
                    <button 
                      onClick={() => handleSort('type_contact')}
                      className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                    >
                      <span>Type</span>
                      <span className="text-xs">{getSortIcon('type_contact')}</span>
                    </button>
                  </th>
                )}
                {isColumnVisible('opportunite') && (
                  <th className="text-left py-2 px-2 font-semibold text-gray-700">
                    <button 
                      onClick={() => handleSort('opportunite_id')}
                      className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                    >
                      <span>Opp.</span>
                      <span className="text-xs">{getSortIcon('opportunite_id')}</span>
                    </button>
                  </th>
                )}
                {isColumnVisible('operation') && (
                  <th className="text-left py-2 px-2 font-semibold text-gray-700">
                    <button 
                      onClick={() => handleSort('operation_id')}
                      className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                    >
                      <span>Opér.</span>
                      <span className="text-xs">{getSortIcon('operation_id')}</span>
                    </button>
                  </th>
                )}
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('date_creation')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>Créé le</span>
                    <span className="text-xs">{getSortIcon('date_creation')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('date_derniere_mise_a_jour')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>MAJ</span>
                    <span className="text-xs">{getSortIcon('date_derniere_mise_a_jour')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('tarif_transporteur')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>T.T</span>
                    <span className="text-xs">{getSortIcon('tarif_transporteur')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('marge_pourcent')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>M.%</span>
                    <span className="text-xs">{getSortIcon('marge_pourcent')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('marge_ht')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>M.HT</span>
                    <span className="text-xs">{getSortIcon('marge_ht')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('marge_ttc')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>M.TTC</span>
                    <span className="text-xs">{getSortIcon('marge_ttc')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('tarif_client')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>T.C</span>
                    <span className="text-xs">{getSortIcon('tarif_client')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('total_ttc')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>Total</span>
                    <span className="text-xs">{getSortIcon('total_ttc')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('statut')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>Statut Doc</span>
                    <span className="text-xs">{getSortIcon('statut')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('statut_paiement')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>Statut Paie.</span>
                    <span className="text-xs">{getSortIcon('statut_paiement')}</span>
                  </button>
                </th>
                <th className="text-left py-2 px-2 font-semibold text-gray-700">
                  <button 
                    onClick={() => handleSort('reste_a_payer')}
                    className="flex items-center space-x-1 hover:text-emerald-600 transition-colors whitespace-nowrap"
                  >
                    <span>Reste</span>
                    <span className="text-xs">{getSortIcon('reste_a_payer')}</span>
                  </button>
                </th>
                <th className="text-right py-2 px-2 font-semibold text-gray-700 whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDocuments.map((doc) => {
                const pricing = calculateDocumentPricing(doc);
                const paymentStatus = getPaymentStatus(doc);
                
                return (
                  <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-2 px-2">
                      <input
                        type="checkbox"
                        checked={selectedDocuments.some(selectedDoc => selectedDoc.id === doc.id)}
                        onChange={() => onSelectDocument && onSelectDocument(doc)}
                        className="w-3 h-3 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <span className="font-mono text-xs text-gray-600">{doc.reference}</span>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <span className={`text-xs px-2 py-1 rounded ${
                        doc.type === 'devis' ? 'bg-blue-100 text-blue-800' :
                        doc.type === 'bon_commande' ? 'bg-green-100 text-green-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {doc.type === 'devis' ? 'Devis' : 
                         doc.type === 'bon_commande' ? 'Bon de commande' : 'Facture'}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <div className="font-medium text-gray-900 text-xs">{doc.client.name}</div>
                    </td>
                    {isColumnVisible('type_contact') && (
                      <td className="py-2 px-2 text-gray-700">
                        {/* Badge Prospect vs Client basé sur type_contact */}
                        {doc.client?.type_contact === 'Client potentiel' ? (
                          <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 w-fit">
                            🎯 Prospect
                          </span>
                        ) : doc.client?.type_contact === 'Client actuel' ? (
                          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 w-fit">
                            ✅ Client
                          </span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-1 w-fit">
                            📄 Autre
                          </span>
                        )}
                      </td>
                    )}
                    {isColumnVisible('opportunite') && (
                      <td className="py-2 px-2 text-gray-700">
                        {doc.opportunite_id ? (
                          <div className="flex flex-col gap-1">
                            <div className="text-xs bg-emerald-50 text-emerald-700 px-1 py-0.5 rounded inline-block font-mono">
                              {opportunities.find(opp => opp.id === doc.opportunite_id)?.numero_opportunite || doc.opportunite_id.slice(0, 8) + '...'}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                    )}
                    {isColumnVisible('operation') && (
                      <td className="py-2 px-2 text-gray-700">
                        {doc.operation_id ? (
                          <div className="text-xs">
                            <div className="bg-blue-50 text-blue-700 px-1 py-0.5 rounded inline-block">
                              {operations.find(op => op.id === doc.operation_id)?.numero_operation || '-'}
                            </div>
                            {doc.transporteur_name && (
                              <div className="bg-orange-50 text-orange-700 px-1 py-0.5 rounded mt-0.5 inline-block text-xs">
                                {doc.transporteur_name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </td>
                    )}
                    <td className="py-2 px-2 text-gray-700">
                      <div className="text-xs">
                        {new Date(doc.date_creation || doc.date).toLocaleString('fr-FR', {
                          year: 'numeric',
                          month: '2-digit', 
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {doc.est_nouveau && (
                          <span className="ml-1 px-1 py-0.5 bg-red-100 text-red-800 text-xs rounded">
                            New
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <div className="text-xs">
                        {new Date(doc.date_derniere_mise_a_jour || doc.updated_at || doc.date).toLocaleString('fr-FR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit', 
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <div className="text-xs font-medium text-blue-600 whitespace-nowrap">
                        {pricing.totalTarifTransporteur.toLocaleString()}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <div className="text-xs font-medium text-purple-600 whitespace-nowrap">
                        {pricing.margeMovenPondere.toFixed(1)}%
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <div className="text-xs font-medium text-emerald-600 whitespace-nowrap">
                        {pricing.totalMargeHt.toLocaleString()}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <div className="text-xs font-medium text-emerald-700 whitespace-nowrap">
                        {pricing.totalMargeAvecTva.toLocaleString()}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <div className="text-xs font-medium text-orange-600 whitespace-nowrap">
                        {pricing.totalTarifClient.toLocaleString()}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-gray-700">
                      <div className="text-xs font-medium text-emerald-600 whitespace-nowrap">
                        {doc.total_ttc.toLocaleString()}
                      </div>
                    </td>
                    {/* Colonne 1: Statut du document */}
                    <td className="py-2 px-2 text-gray-700">
                      <StatusBadge 
                        status={doc.document_status || doc.statut} 
                        documentType={doc.type}
                      />
                    </td>
                    
                    {/* Colonne 2: Statut de paiement */}
                    <td className="py-2 px-2 text-gray-700">
                      {doc.type !== 'devis' && (doc.payment_status || paymentStatus) ? (
                        <StatusBadge 
                          status={doc.payment_status || paymentStatus?.status}
                          documentType={doc.type}
                          isPaymentStatus={true}
                        />
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    
                    {/* Colonne 3: Reste à payer */}
                    <td className="py-2 px-2 text-gray-700">
                      {doc.type !== 'devis' && (doc.reste_a_payer || 0) > 0 ? (
                        <div className="text-xs font-medium text-red-600 whitespace-nowrap">
                          {(doc.reste_a_payer || 0).toLocaleString()}
                        </div>
                      ) : doc.type !== 'devis' && (doc.reste_a_payer || 0) === 0 && (doc.montant_paye || 0) > 0 ? (
                        <div className="text-xs font-medium text-green-600 whitespace-nowrap">
                          0
                        </div>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="py-2 px-2">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => downloadPDF(doc.id, doc.reference)}
                          className="p-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors"
                          title="Télécharger PDF"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDuplicateDocument(doc)}
                          className="p-1 text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded transition-colors"
                          title="Dupliquer le document"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleSendEmail(doc)}
                          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                          title="Envoyer par email"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDocumentView(doc)}
                          className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
                          title="Voir détails"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {onEditDocument && (
                          <button
                            onClick={() => onEditDocument(doc)}
                            className="p-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded transition-colors"
                            title="Modifier"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => openStatusModal(doc)}
                          className="p-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors"
                          title="Changer statut"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                        {onOpenPaymentManager && doc.type !== 'devis' && (
                          <button
                            onClick={() => onOpenPaymentManager(doc)}
                            className="p-1 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded transition-colors"
                            title={`Gérer ${doc.type === 'facture' ? 'encaissement' : 'paiement'}`}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => openDeleteModal(doc)}
                          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Supprimer"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {filteredDocuments.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📄</div>
            <p>Aucun document trouvé</p>
          </div>
        )}

        {/* Pagination */}
        {filteredDocuments.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredDocuments.length / itemsPerPage)}
            totalItems={filteredDocuments.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
              // Scroll to top when changing page
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              setCurrentPage(1); // Reset to first page when changing items per page
            }}
          />
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && selectedDocumentForEmail && (
        <EmailModal
          document={selectedDocumentForEmail}
          user={user}
          onClose={() => setShowEmailModal(false)}
          onEmailSent={handleEmailSent}
        />
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedDocumentForStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Changer le statut</h3>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Document: {selectedDocumentForStatus.reference}</h4>
                <p className="text-sm text-gray-600">
                  Statut actuel: <span className="font-medium">{selectedDocumentForStatus.status || selectedDocumentForStatus.statut || 'Non défini'}</span>
                </p>
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Nouveau statut:</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => {
                      handleStatusChange(selectedDocumentForStatus, 'brouillon');
                      setShowStatusModal(false);
                    }}
                    className="bg-gray-100 text-gray-800 px-3 py-2 rounded text-sm hover:bg-gray-200 transition-colors"
                  >
                    📝 Brouillon
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange(selectedDocumentForStatus, 'envoyé');
                      setShowStatusModal(false);
                    }}
                    className="bg-blue-100 text-blue-800 px-3 py-2 rounded text-sm hover:bg-blue-200 transition-colors"
                  >
                    📤 Envoyé
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange(selectedDocumentForStatus, 'acceptée');
                      setShowStatusModal(false);
                    }}
                    className="bg-green-100 text-green-800 px-3 py-2 rounded text-sm hover:bg-green-200 transition-colors"
                  >
                    ✅ Acceptée
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange(selectedDocumentForStatus, 'refusé');
                      setShowStatusModal(false);
                    }}
                    className="bg-red-100 text-red-800 px-3 py-2 rounded text-sm hover:bg-red-200 transition-colors"
                  >
                    ❌ Refusé
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange(selectedDocumentForStatus, 'expiré');
                      setShowStatusModal(false);
                    }}
                    className="bg-orange-100 text-orange-800 px-3 py-2 rounded text-sm hover:bg-orange-200 transition-colors"
                  >
                    ⏰ Expiré
                  </button>
                  <button
                    onClick={() => {
                      handleStatusChange(selectedDocumentForStatus, 'annulé');
                      setShowStatusModal(false);
                    }}
                    className="bg-gray-100 text-gray-800 px-3 py-2 rounded text-sm hover:bg-gray-200 transition-colors"
                  >
                    🚫 Annulé
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedDocumentForDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-red-600">Confirmer la suppression</h2>
              <button 
                onClick={closeDeleteModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Êtes-vous sûr de vouloir supprimer ce document ?
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-medium text-gray-900">
                  {selectedDocumentForDelete.reference}
                </p>
                <p className="text-sm text-gray-600">
                  {selectedDocumentForDelete.type} - {selectedDocumentForDelete.client?.name}
                </p>
              </div>
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">
                  Cette action est irréversible. Le document sera définitivement supprimé.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isDeleting}
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteDocument}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Barre d'actions en masse */}
      <BulkActionsBar 
        selectedCount={selectedDocuments.length}
        onAction={handleBulkAction}
      />

      {/* Modal Phase 2 - Gestionnaire de colonnes */}
      {showColumnManager && (
        <ColumnManager
          columns={allColumns}
          visibleColumns={visibleColumns}
          onColumnsChange={handleColumnsChange}
          onClose={() => setShowColumnManager(false)}
        />
      )}

      {/* Modal Phase 2 - Vues sauvegardées */}
      {showSavedViews && (
        <SavedViews
          currentFilters={{
            quickFilters: quickFilters,
            searchTerm: globalSearchTerm
          }}
          currentSort={{
            field: sortField,
            order: sortOrder
          }}
          visibleColumns={visibleColumns}
          onLoadView={handleLoadView}
          onClose={() => setShowSavedViews(false)}
        />
      )}

      {/* Modal Phase 2 - Export avancé */}
      {showAdvancedExport && (
        <AdvancedExport
          documents={filteredDocuments}
          selectedDocuments={selectedDocuments}
          visibleColumns={visibleColumns}
          onClose={() => setShowAdvancedExport(false)}
        />
      )}
    </div>
  );
};

export default DocumentList;